import React, { useState, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const ImageCropper = forwardRef((props, ref) => {
  const {
    aspectRatio,           // Optional aspect ratio - no default to allow free cropping
    circleMode = false,    // Enable circular crop mode
    lockAspectRatio,       // Optional - no default value to allow free cropping
    acceptedFileTypes = ['image/jpeg', 'image/png', 'image/webp'],
    cropperWidth = 90,     // Width of the cropper in percentage
  } = props;
  
  // State for storing the selected image 
  const [imgSrc, setImgSrc] = useState('');
  
  // State for storing crop configuration
  const [crop, setCrop] = useState();
  
  // State for completed crop
  const [completedCrop, setCompletedCrop] = useState(null);
  
  // State for preview URL
  const [previewUrl, setPreviewUrl] = useState('');
  
  // State to track if a file has been uploaded
  const [hasFile, setHasFile] = useState(false);
  
  // Reference to the image element
  const imgRef = useRef(null);
  
  // Reference to the file input element to reset it
  const fileInputRef = useRef(null);
  
  // Create a unique ID for this instance
  const instanceId = useRef(`image-cropper-${Math.random().toString(36).substring(2, 9)}`);
  
  // Internal ref for storing cropped data
  const internalRef = useRef({
    files: []
  });
  
  // Expose the files reference to parent component
  useImperativeHandle(ref, () => internalRef.current);
  
  // Parse aspect ratio from string or number
  const getAspectRatioValue = () => {
    // Return undefined if no aspect ratio is provided to allow free cropping
    if (!aspectRatio && !circleMode) {
      return undefined;
    }
    
    if (typeof aspectRatio === 'number') {
      return aspectRatio;
    }
    
    // Handle common aspect ratio strings
    const ratioMap = {
      '1:1': 1,
      '4:3': 4/3,
      '16:9': 16/9,
      '3:2': 3/2,
      '2:3': 2/3,
    };
    
    if (circleMode) {
      return 1; // Circles are always 1:1
    }
    
    return ratioMap[aspectRatio] || undefined; // Default to undefined for free cropping
  };
  
  // Function to handle file selection
  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Check if file type is allowed
      if (!acceptedFileTypes.includes(file.type)) {
        alert(`File type not supported. Please use ${acceptedFileTypes.join(', ')}`);
        return;
      }
      
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImgSrc(reader.result);
        setHasFile(true);
      });
      reader.readAsDataURL(file);
      
      // Store original file information
      internalRef.current.originalFile = file;
      
      // Reset preview
      setPreviewUrl('');
    }
  };
  
  // Function to apply crop when image loads
  const onImageLoad = useCallback((img) => {
    imgRef.current = img;
    
    const { width, height } = img;
    const aspect = getAspectRatioValue();
    
    // If we have a specific aspect ratio, create a centered crop
    if (aspect) {
      const crop = centerCrop(
        makeAspectCrop(
          {
            unit: '%',
            width: cropperWidth,
          },
          aspect,
          width,
          height
        ),
        width,
        height
      );
      setCrop(crop);
    } else {
      // For free cropping, just set a default size selection
      // without enforcing any aspect ratio
      setCrop({
        unit: '%',
        width: cropperWidth,
        height: cropperWidth * (height / width),
        x: (100 - cropperWidth) / 2,
        y: (100 - (cropperWidth * (height / width))) / 2
      });
    }
  }, [cropperWidth]);
  
  // Function to generate the cropped image
  const generateCroppedImage = useCallback(() => {
    if (!completedCrop || !imgRef.current) return;
    
    const canvas = document.createElement('canvas');
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
    
    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;
    
    const ctx = canvas.getContext('2d');
    
    // For circle crop, create a circular clipping path
    if (circleMode) {
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const radius = Math.min(centerX, centerY);
      
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
      ctx.clip();
    }
    
    ctx.drawImage(
      imgRef.current,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY
    );
    
    // Get the original file type
    const originalFile = internalRef.current.originalFile;
    const fileType = originalFile ? originalFile.type : 'image/jpeg';
    
    // Convert canvas to blob using the original file type
    canvas.toBlob((blob) => {
      if (!blob) return;
      
      // Create a File object from the blob
      const fileName = originalFile 
        ? originalFile.name.replace(/\.[^/.]+$/, '') + '-cropped' + getFileExtension(fileType)
        : 'cropped-image' + getFileExtension(fileType);
      
      // Create a File object that mimics the structure of a file input
      const croppedFile = new File([blob], fileName, { 
        type: fileType,
        lastModified: new Date().getTime()
      });
      
      // Store the cropped file in the ref just like a FileList would
      internalRef.current.files = [croppedFile];
      
      // Set the preview URL in state
      const newPreviewUrl = URL.createObjectURL(blob);
      setPreviewUrl(newPreviewUrl);
      
    }, fileType);
  }, [completedCrop, circleMode]);
  
  // Function to remove the current image
  const removeImage = () => {
    // Clear image source
    setImgSrc('');
    
    // Clear preview
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl('');
    }
    
    // Reset crop
    setCrop(undefined);
    setCompletedCrop(null);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    // Clear ref data
    internalRef.current.files = [];
    internalRef.current.originalFile = null;
    
    // Reset file status
    setHasFile(false);
  };
  
  // Helper function to get file extension from mime type
  const getFileExtension = (mimeType) => {
    const extensions = {
      'image/jpeg': '.jpg',
      'image/png': '.png',
      'image/webp': '.webp'
    };
    return extensions[mimeType] || '.jpg';
  };
  
  // Get accepted file types for input element
  const getAcceptedFileTypesString = () => {
    return acceptedFileTypes.join(',');
  };

  // Determine if aspect ratio should be locked
  const shouldLockAspectRatio = () => {
    // If lockAspectRatio prop is explicitly provided, use that
    if (lockAspectRatio !== undefined) {
      return lockAspectRatio;
    }
    
    // Otherwise, only lock if using circle mode
    return circleMode;
  };
  
  return (
    <div className="image-cropper">
      <div className="controls">
        <input 
          ref={fileInputRef}
          type="file" 
          accept={getAcceptedFileTypesString()} 
          onChange={onSelectFile} 
          id={`file-input-${instanceId.current}`}
        />
      </div>
      
      {imgSrc ? (
        <div className="crop-container">
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={getAspectRatioValue()}
            circularCrop={circleMode}
            locked={shouldLockAspectRatio()}
          >
            <img
              src={imgSrc}
              onLoad={(e) => onImageLoad(e.currentTarget)}
              alt="Crop me"
            />
          </ReactCrop>
          
          <div className="button-row">
            <button 
              onClick={generateCroppedImage} 
              disabled={!completedCrop}
              className="crop-button"
            >
              Upload & Preview
            </button>
            
            <button 
              onClick={removeImage}
              className="remove-button"
            >
              Remove Image
            </button>
          </div>
        </div>
      ) : null}
      
      {previewUrl && (
        <div className="preview-container">
          <div className="preview-header">
            <h3>Cropped Preview</h3>
            <button 
              onClick={removeImage}
              className="remove-x-button"
              aria-label="Remove image"
            >
              ✕
            </button>
          </div>
          <img 
            src={previewUrl} 
            alt="Crop preview"
            className="preview-image"
          />
        </div>
      )}
      <style jsx>{`
        .image-cropper {
          display: flex;
          flex-direction: column;
          gap: 20px;
          max-width: 800px;
          margin: 0 auto;
          padding: 15px;
          border: 1px solid #eee;
          border-radius: 8px;
          margin-bottom: 30px;
        }
        
        .controls {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-bottom: 20px;
        }
        
        .crop-container {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }
        
        .button-row {
          display: flex;
          gap: 10px;
        }
        
        .crop-button {
          padding: 8px 16px;
          background-color: #007bff;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-weight: bold;
        }
        
        .crop-button:disabled {
          background-color: #cccccc;
          cursor: not-allowed;
        }
        
        .remove-button {
          padding: 8px 16px;
          background-color: #dc3545;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-weight: bold;
        }
        
        .preview-container {
          margin-top: 20px;
          position: relative;
          border: 1px solid #eee;
          border-radius: 4px;
          padding: 15px;
        }
        
        .preview-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }
        
        .preview-header h3 {
          margin: 0;
        }
        
        .remove-x-button {
          background-color: #dc3545;
          color: white;
          border: none;
          border-radius: 50%;
          width: 25px;
          height: 25px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 12px;
          padding: 0;
        }
        
        .preview-image {
          max-width: 300px;
          max-height: 300px;
          border: 1px dashed #ccc;
        }
      `}</style>
    </div>
  );
});
export default ImageCropper;